import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LEGAL_PATHS, type LegalLang } from '../pages/legalShared'
import { useCompany } from '../hooks/useCompanyInfo'
import PaymentLogos from './PaymentLogos'

// Storefront footer carrying every legal document plus the seller's identity.
// A bank's site review checks that these links exist, are reachable from the
// buying flow, and that the company behind the site is identifiable.
export default function StorefrontFooter({ appName = 'Provisyer' }: { appName?: string }) {
  const { t, i18n } = useTranslation()
  const lang: LegalLang = i18n.language?.startsWith('tr') ? 'tr' : 'en'
  const paths = LEGAL_PATHS[lang]
  const company = useCompany()
  const year = new Date().getFullYear()

  // Contact details only. The legal name is deliberately left out: on a sole
  // proprietorship it is the owner's own name, and the footer is not the place
  // for it. The full seller identity lives on the künye and distance-sales
  // pages, both linked below.
  const address = [company['company.address'], company['company.city']].filter(Boolean).join(', ')

  return (
    <footer className="relative border-t border-black/[0.06] bg-white/60 mt-auto">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
          <div className="max-w-sm">
            <Link to="/" className="text-base font-bold text-gray-900">{appName}</Link>
            {address && <p className="mt-2 text-xs text-gray-500">{address}</p>}
            {company['company.phone'] && (
              <p className="text-xs text-gray-500">
                <a href={`tel:${company['company.phone'].replace(/\s/g, '')}`}>{company['company.phone']}</a>
              </p>
            )}
            {company['company.email'] && (
              <p className="text-xs text-gray-500">
                <a href={`mailto:${company['company.email']}`}>{company['company.email']}</a>
              </p>
            )}
            {company['company.mersis_no'] && (
              <p className="text-xs text-gray-400 mt-1">MERSİS: {company['company.mersis_no']}</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t('footer.legalHeading')}
            </span>
            <Link className="text-sm text-gray-600 hover:text-gray-900" to={paths.about}>{t('footer.about')}</Link>
            <Link className="text-sm text-gray-600 hover:text-gray-900" to={paths.distance}>{t('footer.distanceSales')}</Link>
            <Link className="text-sm text-gray-600 hover:text-gray-900" to={paths.delivery}>{t('footer.deliveryReturn')}</Link>
            <Link className="text-sm text-gray-600 hover:text-gray-900" to={paths.privacy}>{t('footer.privacy')}</Link>
            <Link className="text-sm text-gray-600 hover:text-gray-900" to={paths.terms}>{t('footer.terms')}</Link>
          </div>

          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 block mb-3">
              {t('footer.paymentHeading')}
            </span>
            <PaymentLogos withCaption />
          </div>
        </div>

        <p className="mt-8 text-xs text-gray-400">
          © {year} {appName}. {t('footer.notAffiliated')}
        </p>
      </div>
    </footer>
  )
}
